'use client'

import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { DEFAULT_BRANDING, type BrandingSettings } from '@/lib/branding-defaults'

interface BrandingContextValue extends BrandingSettings {
  loading: boolean
  refresh: () => Promise<void>
}

const BrandingContext = createContext<BrandingContextValue>({
  ...DEFAULT_BRANDING,
  loading: true,
  refresh: async () => {},
})

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const [branding, setBranding] = useState<BrandingSettings>(DEFAULT_BRANDING)
  const [loading, setLoading] = useState(true)

  const refresh = async () => {
    try {
      const res = await fetch('/api/branding', { cache: 'no-store' })
      if (!res.ok) return
      const data = await res.json()
      setBranding({
        logoWhite: data.logoWhite || DEFAULT_BRANDING.logoWhite,
        logoBlackBg: data.logoBlackBg || null,
        favicon: data.favicon || null,
      })
    } catch {
      // keep defaults
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
  }, [])

  useEffect(() => {
    if (!branding.favicon) return
    let link = document.querySelector<HTMLLinkElement>("link[rel='icon']")
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.href = branding.favicon
  }, [branding.favicon])

  const value = useMemo(
    () => ({ ...branding, loading, refresh }),
    [branding, loading],
  )

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>
}

export function useBranding() {
  return useContext(BrandingContext)
}
